import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import RepairSiteCard from '@/components/RepairSiteCard';
import { SR_STATUS_CONFIG, WS_STATUS_CONFIG } from '@/config/statusWorkflow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  FileText,
  MapPin,
  LayoutGrid,
  List,
  Calendar,
  ChevronRight,
  FilePlus,
  FileCheck,
  Info
} from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const DashboardPage = () => {
  const { user, api, currentFacility, hasRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [scheduleDay, setScheduleDay] = useState('today'); // 'today' or 'tomorrow'

  // Period stats state
  const [periodStats, setPeriodStats] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(true);
  const [dateRange, setDateRange] = useState('this_month'); // 'today', 'this_week', 'this_month', 'last_month', 'custom'
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  // Phase 2: All Dashboard scope is derived from the main top-left facility
  // selector (`currentFacility`). The old inner "This Facility / All Facilities"
  // toggle has been removed.
  const isAllFacilities = currentFacility === 'all';
  const showFacilityColumn = isAllFacilities && hasRole(['ADMIN', 'MANAGER']);

  // Calculate date range based on selection
  const getDateRangeValues = useCallback(() => {
    const today = new Date();
    switch (dateRange) {
      case 'today':
        return { start: startOfDay(today), end: endOfDay(today) };
      case 'this_week':
        return { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfWeek(today, { weekStartsOn: 1 }) };
      case 'this_month':
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case 'custom':
        return { 
          start: customStartDate || startOfMonth(today), 
          end: customEndDate || endOfMonth(today) 
        };
      default:
        return { start: startOfMonth(today), end: endOfMonth(today) };
    }
  }, [dateRange, customStartDate, customEndDate]);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const params = currentFacility && currentFacility !== 'all' 
          ? { facility_id: currentFacility } 
          : {};
        // Include schedule_day parameter for today/tomorrow toggle
        params.schedule_day = scheduleDay;
        // Pass the browser's local date so the backend resolves "today" against
        // the user's wall clock (fixes the evening-UTC-rollover bug where work
        // scheduled for today disappeared and tomorrow's appeared instead).
        {
          const _d = new Date();
          params.client_date = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
        }
        const response = await api.get('/dashboard/stats', { params });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        if (error.response?.status !== 402) {
          toast.error('Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [api, currentFacility, scheduleDay]);

  // Fetch period stats
  useEffect(() => {
    const fetchPeriodStats = async () => {
      setPeriodLoading(true);
      try {
        const { start, end } = getDateRangeValues();
        const params = {
          start_date: format(start, 'yyyy-MM-dd'),
          end_date: format(end, 'yyyy-MM-dd'),
          all_facilities: isAllFacilities,
        };
        if (!isAllFacilities && currentFacility) {
          params.facility_id = currentFacility;
        }
        const response = await api.get('/dashboard/period-stats', { params });
        setPeriodStats(response.data);
      } catch (error) {
        console.error('Failed to fetch period stats:', error);
      } finally {
        setPeriodLoading(false);
      }
    };

    fetchPeriodStats();
  }, [api, dateRange, customStartDate, customEndDate, isAllFacilities, currentFacility, getDateRangeValues]);

  const getDaysOpenClass = (days) => {
    if (days <= 3) return 'days-badge-good';
    if (days <= 7) return 'days-badge-warning';
    return 'days-badge-critical';
  };

  // Get display text for current date range
  const getDateRangeDisplay = () => {
    const { start, end } = getDateRangeValues();
    if (dateRange === 'today') return 'Today';
    if (dateRange === 'this_week') return 'This Week';
    if (dateRange === 'this_month') return 'This Month';
    if (dateRange === 'last_month') return 'Last Month';
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-loading">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  const statusCounts = stats?.status_counts || {};
  const todaysJobs = stats?.todays_jobs || stats?.active_jobs || [];
  const repairSiteCards = stats?.repair_site_cards || [];

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#323033]">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-sm sm:text-base text-[#5A5A5A] mt-1">
          Here&apos;s an overview of your rail operations
        </p>
      </div>

      {/* Service Requests in Period Widget - Replaces old stats grid */}
      <Card data-testid="period-stats-widget">
        <CardHeader className="pb-3 sm:pb-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            <CardTitle className="text-base sm:text-lg font-semibold text-[#323033]">
              Service Requests in Period
            </CardTitle>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              {/* Date Range Selector */}
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="date-range-select">
                  <Calendar className="w-4 h-4 mr-2 text-[#5A5A5A]" />
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>

              {/* Custom Date Range Pickers */}
              {dateRange === 'custom' && (
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-[120px] justify-start text-left font-normal">
                        {customStartDate ? format(customStartDate, 'MMM d, yyyy') : 'Start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={customStartDate}
                        onSelect={setCustomStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <span className="text-[#5A5A5A]">to</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-[120px] justify-start text-left font-normal">
                        {customEndDate ? format(customEndDate, 'MMM d, yyyy') : 'End date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={customEndDate}
                        onSelect={setCustomEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs sm:text-sm text-[#5A5A5A] mt-1">{getDateRangeDisplay()}</p>
        </CardHeader>
        <CardContent className="pt-0 sm:pt-2">
          {periodLoading ? (
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <Skeleton className="h-24 flex-1 rounded-lg" />
              <Skeleton className="h-24 flex-1 rounded-lg" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* NEW Service Requests */}
              <div 
                className="p-4 sm:p-6 rounded-lg bg-[#005EF8]/5 border border-[#005EF8]/20"
                data-testid="period-new-count"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-[#005EF8]">New Service Requests</p>
                    <p className="text-3xl sm:text-4xl font-bold text-[#323033] mt-1 sm:mt-2">
                      {periodStats?.new_count ?? 0}
                    </p>
                    <p className="text-xs text-[#5A5A5A] mt-1">Created in this period</p>
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#005EF8]/10 rounded-lg flex items-center justify-center">
                    <FilePlus className="w-6 h-6 sm:w-7 sm:h-7 text-[#005EF8]" />
                  </div>
                </div>
              </div>

              {/* CLOSED Service Requests */}
              <div 
                className="p-4 sm:p-6 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20"
                data-testid="period-closed-count"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-[#10B981]">Closed Service Requests</p>
                    <p className="text-3xl sm:text-4xl font-bold text-[#323033] mt-1 sm:mt-2">
                      {periodStats?.closed_count ?? 0}
                    </p>
                    <p className="text-xs text-[#5A5A5A] mt-1">Closed in this period</p>
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#10B981]/10 rounded-lg flex items-center justify-center">
                    <FileCheck className="w-6 h-6 sm:w-7 sm:h-7 text-[#10B981]" />
                  </div>
                </div>
                {/* Disclaimer */}
                <div className="mt-2 sm:mt-3 flex items-start gap-1.5 text-xs text-[#6B7280]">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>Cancelled requests are not included in Closed totals.</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Work Schedule - Repair Site grouped card view (default) with table toggle */}
      <Card data-testid="todays-work-schedule-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <CardTitle className="text-base sm:text-lg font-semibold text-[#323033]">
              {scheduleDay === 'today' ? "Today's" : "Tomorrow's"} Work Schedule
            </CardTitle>
            {/* Today/Tomorrow Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit" data-testid="schedule-day-toggle">
              <Button
                variant={scheduleDay === 'today' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setScheduleDay('today')}
                className={`text-xs sm:text-sm ${scheduleDay === 'today' ? 'bg-[#005EF8] text-white' : 'text-gray-600'}`}
                data-testid="schedule-today-btn"
              >
                <Calendar className="w-4 h-4 mr-1 hidden sm:block" />
                Today
              </Button>
              <Button
                variant={scheduleDay === 'tomorrow' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setScheduleDay('tomorrow')}
                className={`text-xs sm:text-sm ${scheduleDay === 'tomorrow' ? 'bg-[#005EF8] text-white' : 'text-gray-600'}`}
                data-testid="schedule-tomorrow-btn"
              >
                <ChevronRight className="w-4 h-4 mr-1 hidden sm:block" />
                Tomorrow
              </Button>
            </div>
          </div>
          {/* View toggle buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
              data-testid="view-cards-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Sites
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              data-testid="view-table-btn"
            >
              <List className="w-4 h-4 mr-1" />
              Table
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Card View - Grouped by Repair Site (default) */}
          {viewMode === 'cards' && (
            <>
              {repairSiteCards.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {repairSiteCards.map((site, index) => (
                    <RepairSiteCard
                      key={site.repair_site || index}
                      site={site}
                      index={index}
                      showFacility={showFacilityColumn}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
                  <p className="text-[#5A5A5A]">No work scheduled for {scheduleDay === 'today' ? 'today' : 'tomorrow'}</p>
                </div>
              )}
            </>
          )}

          {/* Table View - Flat list of cars */}
          {viewMode === 'table' && (
            <>
              {todaysJobs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {showFacilityColumn && <th>Facility</th>}
                        <th>SR #</th>
                        <th>Car</th>
                        <th>Event Type</th>
                        <th>Repair Site</th>
                        <th>Job Lead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todaysJobs.map((job) => (
                        <tr key={job.id} data-testid={`job-row-${job.id}`}>
                          {showFacilityColumn && <td>{job.facility_name || '-'}</td>}
                          <td className="font-medium text-[#005EF8]">{job.sr_number || job.job_number || '-'}</td>
                          <td>
                            <span className="font-medium">{job.car_mark || ''}</span>
                            {job.car_mark && job.car_number && <span className="text-[#5A5A5A] ml-1">{job.car_number}</span>}
                            {!job.car_mark && !job.car_number && <span className="text-[#5A5A5A]">-</span>}
                          </td>
                          <td>{job.event_type || '-'}</td>
                          <td>{job.repair_site || '-'}</td>
                          <td>{job.job_lead_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
                  <p className="text-[#5A5A5A]">No work scheduled for {scheduleDay === 'today' ? 'today' : 'tomorrow'}</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Status Overview - wired to canonical statusWorkflow.js so labels and
          colors always match the SR list, Work Schedule views, Car File, and
          Work Order History. */}
      <Card data-testid="status-overview-card">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg font-semibold text-[#323033]">
            Service Requests by Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            <StatusCard status="NEW"        count={statusCounts.NEW || 0} />
            <StatusCard status="SCHEDULED"  count={statusCounts.SCHEDULED || statusCounts.SR_SCHEDULED || 0} />
            <StatusCard status="IN_PROCESS" count={statusCounts.IN_PROCESS || 0} />
            <StatusCard status="ON_HOLD"    count={statusCounts.ON_HOLD || 0} />
            <StatusCard status="QA_REVIEW"  count={statusCounts.QA_REVIEW || 0} />
            <StatusCard status="CLOSED"     count={statusCounts.CLOSED || 0} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Canonical status chip lookup: prefer WS config (post-scheduling), fall
// back to SR config for pre-scheduling states. Guarantees identical colors
// with the rest of the app.
const getCanonicalStatusConfig = (status) =>
  WS_STATUS_CONFIG[status] || SR_STATUS_CONFIG[status] || {
    label: status,
    color: 'bg-gray-100 text-gray-700 border-gray-200',
  };

const StatusCard = ({ status, count }) => {
  const cfg = getCanonicalStatusConfig(status);
  return (
    <div
      className="p-3 sm:p-4 rounded-lg border border-border hover:border-primary/30 transition-colors"
      data-testid={`status-${status.toLowerCase()}`}
    >
      <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
        <span
          className={`status-badge text-xs inline-flex items-center rounded px-2 py-0.5 border ${cfg.color}`}
          data-testid={`status-chip-${status.toLowerCase()}`}
        >
          {cfg.label}
        </span>
      </div>
      <p className="text-xl sm:text-2xl font-bold text-[#323033]">{count}</p>
    </div>
  );
};

export default DashboardPage;
